import { Button, HeaderBlock } from '@components'
import React from 'react'
import { FaArrowLeft } from 'react-icons/fa'
import styled from 'styled-components'

type MenuItem = {
  Icon?: any
  name?: string
  onClick?: () => any
  intent?: any
  disabled?: boolean
}

interface Props {
  title?: React.ReactNode
  subTitle?: React.ReactNode
  badges?: React.ReactNode
  menuItems?: MenuItem[] | null
  headerActions?: React.ReactNode
  footer?: React.ReactNode
  contentPadding?: 'none' | 'md'
  contentOverflow?: 'auto' | 'hidden'
  headerContentInset?: 'md'
  onBack?: () => void
  backAriaLabel?: string
  children: React.ReactNode
}

export const SettingsSubPageLayout: React.FC<Props> = ({
  title,
  subTitle = '',
  badges,
  menuItems,
  headerActions,
  footer,
  contentPadding = 'md',
  contentOverflow = 'auto',
  headerContentInset,
  onBack,
  backAriaLabel,
  children
}) => {
  const showHeader = Boolean(
    title || (menuItems && menuItems.length > 0) || badges || onBack || headerActions
  )

  return (
    <Wrapper>
      {showHeader && (
        <HeaderRow>
          {onBack ? (
            <BackButton
              type="button"
              isSecondary
              icon={<FaArrowLeft />}
              onClick={onBack}
              aria-label={backAriaLabel}
            />
          ) : null}
          <HeaderMain>
            <HeaderBlock
              header={title}
              subHeader={subTitle}
              badges={badges}
              menuItems={menuItems ?? undefined}
              contentInset={headerContentInset}
              withDivider
            />
          </HeaderMain>
          {headerActions ? <HeaderActions>{headerActions}</HeaderActions> : null}
        </HeaderRow>
      )}
      <Content $contentPadding={contentPadding} $contentOverflow={contentOverflow}>
        {children}
      </Content>
      {footer ? <Footer>{footer}</Footer> : null}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  background: white;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
`

const HeaderRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  flex-shrink: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

const HeaderMain = styled.div`
  flex: 1;
  min-width: 0;

  > div {
    border-bottom: none;
    height: 100%;
    box-sizing: border-box;
  }
`

const HeaderActions = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  flex-shrink: 0;
  padding: ${({ theme }) => theme.spacing.md}rem ${({ theme }) => theme.spacing.lg}rem
    ${({ theme }) => theme.spacing.md}rem 0;
`

const BackButton = styled(Button)`
  margin-left: ${({ theme }) => theme.spacing.md}rem;
  flex-shrink: 0;
`

const Content = styled.div<{
  $contentPadding: 'none' | 'md'
  $contentOverflow: 'auto' | 'hidden'
}>`
  flex: 1;
  min-height: 0;
  overflow: ${({ $contentOverflow }) => $contentOverflow};
  padding: ${({ theme, $contentPadding }) =>
    $contentPadding === 'md' ? `${theme.spacing.md}rem` : '0'};
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  flex-shrink: 0;
  padding: ${({ theme }) => theme.spacing.md}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
  background: ${({ theme }) => theme.colors.neutralWhite};
`
